import React, { FC, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import { FormattedMessage, injectIntl } from 'react-intl'

import {
  Text,
  Divider,
  IconMapPin,
  IconPackage,
  csx,
  dataAttr,
} from '@vtex/admin-ui'
import { getShippingEstimateMessage } from '@/checkout-instore-legacy/shipping/utils'
import Price from '@/checkout-instore-legacy/components/Commons/Price'
import {
  BottomSliderModal,
  BottomSliderModalCloseButton,
  BottomSliderModalHeader,
} from '@/checkout-instore-legacy/components/Commons/BottomSliderModal'
import RightSideModal from '@/checkout-instore-legacy/components/Commons/RightSideModal'

import { withMediaQuery } from '@/checkout-instore-legacy/utils/mediaQuery'
import { useIntl } from '@/checkout-instore-legacy/hooks/intl'

interface ShippingInfoBoxProps {
  items: any
  label: string
  optionsLabel: string
  unavailableLabel: string
  mediaQuery: (options: {
    phone: boolean
    tablet: boolean
    desktop: boolean
  }) => boolean
}

const ShippingInfoBox: FC<ShippingInfoBoxProps> = ({
  items,
  label,
  optionsLabel,
  unavailableLabel,
  mediaQuery,
}) => {
  const { formatMessage } = useIntl()

  const [isOpen, setIsOpen] = useState(false)

  const isMobile = mediaQuery({
    phone: true,
    tablet: false,
    desktop: false,
  })

  const renderOptions = () => {
    if (items.length > 1) {
      return (
        <div
          className={csx({
            overflowX: 'hidden',
            overflowY: 'scroll',
            marginTop: '$space-3',
          })}
        >
          {items.map((item: any) => {
            return (
              <div key={item.id}>
                <div
                  className={csx({
                    marginLeft: '1.87rem',
                    marginTop: '.75rem',
                    marginBottom: '.75rem',
                    display: 'flex',
                    width: '100%',
                  })}
                >
                  <div
                    className={csx({
                      width: '10%',
                      flexDirection: 'column',
                    })}
                  >
                    {item.store.iconType === 'nc-store' ? (
                      <IconMapPin />
                    ) : (
                      <IconPackage />
                    )}
                  </div>
                  <div
                    className={csx({
                      width: '80%',
                      flexDirection: 'column',
                      '> *': {
                        display: 'block',
                      },
                    })}
                  >
                    <Text
                      data-testid="sla-name"
                      className={csx({ fontSize: 16, fontWeight: 600 })}
                    >
                      {item.store.name}
                    </Text>
                    <Text
                      className={csx({
                        fontSize: '14px',
                        fontWeight: 600,
                        lineHeight: '20px',
                      })}
                    >
                      {`${getShippingEstimateMessage(
                        item.shippingEstimate
                      )} | `}
                      {item.shippingCost ? (
                        // TODO: this error popped up when moving the checkout-instore project
                        // to checkout-instore-legacy, but needs to be fixed at some point
                        // @ts-ignore
                        <Price className="fw6 f6" value={item.shippingCost} />
                      ) : (
                        <Text variant="action1" tone="positive">
                          <FormattedMessage id="product.shippingAid.free" />
                        </Text>
                      )}
                    </Text>
                  </div>
                </div>
                <div
                  className={csx({
                    paddingTop: '.75rem',
                    paddingBottom: '.75rem',
                  })}
                >
                  <Divider
                    className={csx({ width: '90%', marginLeft: '5%' })}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )
    }

    return null
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <div className={csx({ display: 'flex', marginTop: '1.25rem' })}>
      <div
        className={csx({
          marginLeft: '1.75rem',
          flexDirection: 'column',
          width: '70%',
          marginBottom: '1.25rem',
        })}
      >
        {!isEmpty(items) ? (
          <div
            className={csx({
              '> *': {
                display: 'block',
              },
            })}
          >
            <Text className={csx({ fontSize: 14 })} data-testid={label}>
              <FormattedMessage id={label} />
            </Text>
            <Text
              className={csx({ fontSize: 14, fontWeight: 600 })}
              data-testid="shipping-estimate"
            >
              {getShippingEstimateMessage(items[0].shippingEstimate, true)}
            </Text>

            {items.length > 1 ? (
              <div
                className={csx({ marginTop: '1.5rem' })}
                data-testid={`${label}-table`}
              >
                <Text
                  tone="info"
                  className={csx({
                    cursor: 'pointer',
                    [dataAttr('tone', 'info')]: {
                      color: 'blue !important',
                    },
                  })}
                  onClick={() => setIsOpen(true)}
                  data-testid={optionsLabel}
                >
                  {`${items.length} `}
                  <FormattedMessage id={optionsLabel} />{' '}
                </Text>
                {isMobile ? (
                  <BottomSliderModal isOpen={isOpen} onClose={handleClose}>
                    <div
                      className={csx({
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: '100vh',
                      })}
                    >
                      <BottomSliderModalHeader>
                        <Text variant="title1">
                          <FormattedMessage id={optionsLabel} />
                        </Text>
                        <BottomSliderModalCloseButton />
                      </BottomSliderModalHeader>
                      {renderOptions()}
                    </div>
                  </BottomSliderModal>
                ) : (
                  <RightSideModal
                    isOpen={isOpen}
                    title={formatMessage({ id: optionsLabel })}
                    setIsOpen={setIsOpen}
                    children={renderOptions()}
                  />
                )}
              </div>
            ) : null}
          </div>
        ) : (
          <Text data-testid={unavailableLabel}>
            <FormattedMessage id={unavailableLabel} />
          </Text>
        )}
      </div>
      <div
        className={csx({
          flexDirection: 'column',
          width: '30%',
          fontWeight: 600,
        })}
        data-testid="shipping-cost"
      >
        {!isEmpty(items) ? (
          items[0].shippingCost ? (
            // TODO: this error popped up when moving the checkout-instore project
            // to checkout-instore-legacy, but needs to be fixed at some point
            // @ts-ignore
            <Price className="fw6 f6" value={items[0].shippingCost} />
          ) : (
            <Text variant="action2" tone="positive">
              <FormattedMessage id="product.shippingAid.free" />
            </Text>
          )
        ) : null}
      </div>
    </div>
  )
}

// TODO: this error popped up when moving the checkout-instore project
// to checkout-instore-legacy, but needs to be fixed at some point
// @ts-ignore
export default withMediaQuery(injectIntl(ShippingInfoBox))
